import React, { useCallback, useEffect, useState } from 'react';
import Skeleton from '../common/Skeleton';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmDelete } from '../common/confirm';
import {
    Plus, Search, Trash2, AlertTriangle, Save, RotateCcw,
} from 'lucide-react';
import {
    capaService, CapaIssue, CapaIssueCreate, IssueSourceType,
} from '../../services/capaService';
import NoDataFound from '../common/NoDataFound';
import IssueTriageModal from './IssueTriageModal';
import extractApiError from '../../utils/apiError';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { maxLenForCode } from '../../validation/engine';

const SOURCE_TYPES: { value: IssueSourceType; label: string; disabled: boolean }[] = [
    { value: 'Manual',              label: 'Manual',              disabled: false },
    { value: 'Audit',               label: 'Audit (coming soon)', disabled: true  },
    { value: 'Compliance',          label: 'Compliance (coming soon)', disabled: true },
    { value: 'Incident Reporting',  label: 'Incident Reporting (coming soon)', disabled: true },
    { value: 'Control Test',        label: 'Control Test (coming soon)', disabled: true },
    { value: 'Risk Assessment',     label: 'Risk Assessment (coming soon)', disabled: true },
];

const TRIAGE_VARIANT: Record<string, string> = {
    TRIVIAL:       'neutral',
    INVESTIGATION: 'info',
    ACTION_ONLY:   'warning',
};
const TRIAGE_LABELS: Record<string, string> = {
    TRIVIAL:       'Trivial',
    INVESTIGATION: 'In Investigation',
    ACTION_ONLY:   'Action Only',
};
const triageBadge = (outcome: CapaIssue['triage_outcome']) => {
    if (!outcome) return null;
    return (
        <span className={`status-badge sm ${TRIAGE_VARIANT[outcome] ?? 'neutral'}`}>
            {TRIAGE_LABELS[outcome] ?? outcome}
        </span>
    );
};

const IssueTriageTab: React.FC = () => {
    const { allowed: canModify, loading: permLoading } = usePermission('CAP_ISSUE_TRIAGE', 'view+modify');
    const { allowed: canView, loading: viewLoading } = usePermission('CAP_ISSUE_TRIAGE', 'view');
    const [issues, setIssues] = useState<CapaIssue[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [search, setSearch] = useState('');
    const [filterResolved, setFilterResolved] = useState<'all' | 'open' | 'resolved'>('open');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Create-Issue modal (no owner field per R3)
    const [showCreate, setShowCreate] = useState(false);
    const [draft, setDraft] = useState<CapaIssueCreate>({
        issue_code: '', issue_name: '', issue_description: '',
        issue_source_type: 'Manual', is_trivial: false, is_resolved: false,
    });

    // Row click opens the unified triage workbench (mirrors Incident Reporting).
    const [triageFor, setTriageFor] = useState<CapaIssue | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const fieldVh = useFieldValidation();

    const load = useCallback(async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const { data, total } = await capaService.listPaged({
                q: search || undefined,
                resolved: filterResolved === 'all' ? undefined : filterResolved === 'resolved',
                limit: pageSize,
                offset: (page - 1) * pageSize,
            });
            setIssues(data);
            setTotalRecords(total);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load issues') });
        } finally { setLoading(false); }
    }, [search, filterResolved, page, pageSize, canView]);

    useEffect(() => { setPage(1); }, [search, filterResolved, pageSize]);
    useEffect(() => { load(); }, [load]);

    const handleCreateIssue = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const valid = fieldVh.validateAll([
            { name: 'issue_name', code: 109, value: draft.issue_name, required: true },
            { name: 'issue_description', code: 110, value: draft.issue_description },
        ]);
        if (!valid) return;
        try {
            setSubmitting(true);
            await capaService.create({ ...draft, issue_head_user_id: null });
            setShowCreate(false);
            setDraft({
                issue_code: '', issue_name: '', issue_description: '',
                issue_source_type: 'Manual', is_trivial: false, is_resolved: false,
            });
            fieldVh.reset();
            setToast({ type: 'success', message: 'Issue created successfully' });
            load();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to create issue') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleUnmarkTrivial = async (i: CapaIssue) => {
        try {
            await capaService.setTriage(i.id, null);
            setToast({ type: 'success', message: 'Unmarked.' });
            load();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to unmark') });
        }
    };

    const handleDeleteIssue = async (i: CapaIssue) => {
        const ok = await confirmDelete({ text: `Delete issue ${i.issue_code}? This cannot be undone.` });
        if (!ok) return;
        try {
            await capaService.remove(i.id);
            setToast({ type: 'delete', message: 'Issue deleted.' });
            load();
        } catch (e: any) {
            setToast({ type: 'error', message: e?.message || 'Failed to delete' });
        }
    };

    return (
        <div className="netbody-card flex flex-col h-full">

            {(viewLoading || canView) && (
                <div className="netbody-header">
                    <div className="flex items-center gap-2 ml-auto">
                        <FormControl.SearchSelect
                            value={filterResolved}
                            onChange={v => setFilterResolved((v ?? 'open') as 'all' | 'open' | 'resolved')}
                            className="filter-status"
                            data-testid="SEL_capa_issue_status_filter"
                            options={[
                                { value: 'open', label: 'Open only' },
                                { value: 'resolved', label: 'Resolved only' },
                                { value: 'all', label: 'All statuses' },
                            ]}
                            noSearchIcon
                        />
                        <FormControl.Input
                            type="text"
                            placeholder="Search issues…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            leadingIcon={<Search size={14} />}
                            className="filter-search"
                            data-testid="INP_capa_issue_search"
                        />
                        <Button variant="fill" icon={Plus} onClick={() => setShowCreate(true)} disabled={permLoading || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_capa_issue_create">Create</Button>
                    </div>
                </div>
            )}

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    {/* merged: both permission wrapper and direct table */}
                    {!viewLoading && !canView ? (
                        <div style={{ padding: '40px 20px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <NoDataFound title="Not authorised" message="You do not have access to CAPA issue triage." />
                        </div>
                    ) : (
                        <div className="table-list">
                            <div className="table-list-scroll">
                                <table className="table-list-table">
                                    <thead>
                                        <tr>
                                            {/* merged: incoming + existing */}
                                            <th className="table-list-th table-list-cell--sno">S.No</th>
                                            <th className="table-list-th">Issue</th>
                                            <th className="table-list-th">Source</th>
                                            <th className="table-list-th table-list-cell--status">Status</th>
                                            <th className="table-list-th table-list-th--right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody aria-busy={loading} aria-live="polite">
                                        {loading && <Skeleton.TableRows cols={['3xs', 'xs', 'md', 'xs', 'pill', { width: 'icon', align: 'right' }]} />}
                                        {!loading && issues.length === 0 && (
                                                <tr>
                                                    <td colSpan={5} className="table-list-cell" style={{ padding: '40px 20px', textAlign: 'center' }}>
                                                        <NoDataFound
                                                            title={search || filterResolved !== 'open' ? 'No Matching Issues' : 'No Issues Found'}
                                                            message={search || filterResolved !== 'open' ? 'No issues match your search or filter.' : 'Click "Create" to add one.'}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                            {!loading && issues.map((i, idx) => {
                                                const isTrivial = i.triage_outcome === 'TRIVIAL';
                                                return (
                                                    <tr
                                                        key={i.id}
                                                        className="table-list-row"
                                                        onClick={() => setTriageFor(i)}
                                                        data-testid={`capa_issue_row_${i.id}`}
                                                    >
                                                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                        <td className="table-list-cell">
                                                            <span
                                                                className="table-list-cell-title"
                                                                style={{ textDecoration: i.is_resolved ? 'line-through' : 'none', color: i.is_resolved ? '#9ca3af' : undefined }}
                                                            >
                                                                {i.issue_name}
                                                            </span>
                                                            {i.is_trivial && (
                                                                <span className="table-list-cell-sub flex items-center gap-1 text-amber-700">
                                                                    <AlertTriangle size={10} /> Trivial
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="table-list-cell">
                                                            {i.issue_source_type && (
                                                                <span className="status-badge sm info">
                                                                    {i.issue_source_type}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="table-list-cell">
                                                            {triageBadge(i.triage_outcome) || <span className="text-xs text-gray-400">untriaged</span>}
                                                        </td>
                                                        <td className="table-list-cell table-list-cell--actions">
                                                            <div className="flex items-center gap-1 justify-end">
                                                                {isTrivial && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); handleUnmarkTrivial(i); }}
                                                                        className="table-list-action"
                                                                        title={!canModify ? NOT_AUTHORISED_MSG : 'Unmark Trivial'}
                                                                        disabled={permLoading || !canModify}
                                                                        data-testid={`BTN_capa_issue_row_${i.id}_unmark_trivial`}
                                                                    >
                                                                        <RotateCcw size={14} />
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); handleDeleteIssue(i); }}
                                                                    className="table-list-action table-list-action--delete"
                                                                    title={!canModify ? NOT_AUTHORISED_MSG : 'Delete'}
                                                                    disabled={permLoading || !canModify}
                                                                    data-testid={`BTN_capa_issue_row_${i.id}_delete`}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalRecords={totalRecords}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={[10, 25, 50, 100]}
                                recordLabel="issues"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Create Issue Modal — no owner field per R3 */}
            <Modal isOpen={showCreate} onClose={() => { setShowCreate(false); fieldVh.reset(); }} size="md">
                <Modal.Header>Create CAPA Issue</Modal.Header>
                <Modal.Body>
                    <form id="capa-issue-form" onSubmit={handleCreateIssue} className="flex flex-col gap-y-4" data-testid="MOD_capa_issue_form">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                            <FormControl.SearchSelect
                                label="Source Type"
                                options={SOURCE_TYPES.map(s => ({ value: s.value, label: s.label, disabled: s.disabled }))}
                                value={draft.issue_source_type}
                                onChange={v => {
                                    const next = String(v ?? 'Manual') as IssueSourceType;
                                    setDraft(d => ({ ...d, issue_source_type: next }));
                                }}
                                data-testid="SEL_capa_issue_source_type"
                            />
                            <div className="col-span-2">
                                <FormControl.Input
                                    label="Name"
                                    required
                                    value={draft.issue_name}
                                    onChange={e => { setDraft(d => ({ ...d, issue_name: e.target.value })); fieldVh.clearError('issue_name'); }}
                                    onBlur={() => fieldVh.validateField('issue_name', 109, draft.issue_name, { required: true })}
                                    maxLength={maxLenForCode(109)}
                                    error={fieldVh.errors.issue_name}
                                    placeholder="Short title for the issue"
                                    data-testid="INP_capa_issue_name"
                                />
                            </div>
                        </div>
                        <FormControl.Textarea
                            label="Description"
                            value={draft.issue_description}
                            onChange={e => { setDraft(d => ({ ...d, issue_description: e.target.value })); fieldVh.clearError('issue_description'); }}
                            onBlur={() => fieldVh.validateField('issue_description', 110, draft.issue_description || '')}
                            maxLength={maxLenForCode(110)}
                            error={fieldVh.errors.issue_description}
                            placeholder="Describe the issue and why it matters"
                            data-testid="INP_capa_issue_description"
                        />
                        <p className="text-xs text-gray-500">
                            Owner is set later when you Send to Investigation. Trivial issues can be archived directly from the row actions.
                        </p>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" form="capa-issue-form" variant="fill" icon={Save} disabled={submitting} data-testid="BTN_capa_issue_submit">{submitting ? 'Submitting...' : 'Submit'}</Button>
                </Modal.Footer>
            </Modal>

            {/* Unified triage workbench — opened by clicking a row.
                Mirrors the Incident Reporting triage decision UX. */}
            <IssueTriageModal
                isOpen={!!triageFor}
                issue={triageFor}
                onClose={() => setTriageFor(null)}
                onUpdated={() => {
                    setToast({ type: 'success', message: 'Triage decision applied.' });
                    setTriageFor(null);
                    load();
                }}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default IssueTriageTab;
